"use client";

import staticData from "@/public/data/hotel/detail.json";
import { CardContent, CardDescription } from "@/shared/components/common/Card";
import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import Typography from "@/shared/components/common/Typography";
import type { MinPriceRoom } from "@/types/services/hotel/apis/Detail";

interface SidebarcardBodyProps {
  bodyData: {
    stayNight: number;
    checkin: string;
    checkout: string;
    rooms: MinPriceRoom[];
  };
}

export default function SidebarcardBody({
  bodyData: { checkin, checkout, stayNight, rooms },
}: SidebarcardBodyProps) {
  const {
    body: { checkInLabel, checkOutLabel, currencyLabel },
    header: { descriptionLabel },
  } = staticData.sidebarCard;

  return (
    <CardContent className="!p-0">
      {/* Section 1 */}
      <Flex className="flex-col gap-y-6">
        {/* Checkin & Checkout */}
        <Flex className="justify-between w-full">
          <Flex direction={"col"} gap={"3"}>
            {/* Checkin */}
            <Flex className="gap-x-6">
              <Typography
                variant={"body3"}
                fontWeight={"medium"}
                className="text-muted-foreground"
              >
                {checkInLabel}
              </Typography>
              <Typography
                variant={"caption"}
                className="text-ghost font-medium"
              >
                {checkin}
              </Typography>
            </Flex>

            {/* CheckOut */}
            <Flex className="gap-x-6">
              <Typography
                variant={"body3"}
                fontWeight={"medium"}
                className="text-muted-foreground"
              >
                {checkOutLabel}
              </Typography>
              <Typography
                variant={"caption"}
                className="text-ghost font-medium"
              >
                {checkout}
              </Typography>
            </Flex>
          </Flex>

          <CardDescription className="flex gap-x-1 pl-4 font-medium text-ghost">
            <span>{stayNight}</span>

            <span> {descriptionLabel}</span>
          </CardDescription>
        </Flex>

        {/* Rooms */}
        <Flex className="flex-col gap-y-3 text-pretty">
          <List
            items={rooms}
            renderItems={({
              name,
              rate_plans: { name: ratePlan, price },
              count,
            }) => (
              <Flex className="justify-between">
                {/* Room title & sub */}
                <Flex className="items-center gap-x-2 text-wrap">
                  <Typography className="text-foreground font-semibold">
                    {name}
                  </Typography>
                  <Typography className="text-ghost font-medium text-xs">
                    ({ratePlan})
                  </Typography>

                  {count > 1 && (
                    <Flex className="items-center gap-x-2">
                      <PhosphorIcon
                        name="X"
                        size={20}
                        className="text-muted-foreground"
                      />
                      <Typography className="text-ghost font-medium text-xs">
                        {count}
                      </Typography>
                    </Flex>
                  )}
                </Flex>

                {/* Room price */}
                <Flex className="items-center gap-x-1">
                  <Typography
                    variant="h6"
                    className="text-foreground font-semibold text-md"
                  >
                    {price.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="h6"
                    className="text-ghost font-medium text-sm"
                  >
                    {currencyLabel}
                  </Typography>
                </Flex>
              </Flex>
            )}
          />
        </Flex>
      </Flex>
    </CardContent>
  );
}
